import { useTranslation } from 'react-i18next';
import { useState, useRef, useContext, startTransition } from 'react';

import { Collapsible } from './Collapsible';
import { PlatformChooser } from '../../SearchPage/Filter/PlatformChooser'; 
import { SourceGroupsChooser } from './SourceGroupsChooser';
import { ReactComponent as FilterIcon } from '../assets/filter.svg';
import { DoubleRangeSlider } from './DoubleRangeSlider/DoubleRangeSlider';
import { LanguageChooser } from './LanguageChooser';
import { SourceChooser } from './SourceChooser';
import { CountryChooser } from "./CountryChooser";
import { Sentiment } from './Sentiment';
import { ContentTypeChooser } from "./ContentTypeChooser";
import {useClickOutside} from '../../../utils/useClickOutside.jsx'
import './Filter.scss';
import styles from './Filter.module.scss';
import {MessageStatusChooser} from "./MessageStatusChooser";
import {FavoriteChooser} from "../FavoriteChooser";
import { MessagesContext } from '../contexts/MessagesContext';
import { useCurrentUser } from '../../../contexts/CurrentUser';
import { ExcludeSwitch } from '../../SearchPage/Filter/ExcludeSwitch';
import { AffiliationCountries } from '../../SearchPage/Filter/AffiliationCountries';
import { BotsChooser } from './BotsChooser';
import { IoCChooser } from './IoCChooser';
import { AffiliationTypesChooser } from './AffiliationTypesChooser';
import {FeedsChooser} from "../../SearchPage/Filter/FeedsChooser";
import { GeoGroupFilter } from '../../SearchPage/Filter/GeoGroupFilter';
import { TagsHierarchyChooser } from '../../../components/TagsHierarchyChooser/TagsHierarchyChooser';


export const Filter = ({
  isShare,
  narrativeId,
  platformsFilter, 
  sourceGroupsFilter, 
  languagesFilter, 
  sourcesFilter, 
  countriesFilter, 
  sentimentFilter,
  contentTypesFilter,
  messagesStatusFilter,
  showOnlyFavoritesFilter,
  showOnlyBotsFilter,
  botsExcludeFilter,
  discreditationsFilter,
  affiliationCountriesFilter,
  feedsFilter,
  onChange,
  isThreat=false,
  languagesExcludeFilter,
  countriesExcludeFilter,
  sourceGroupsExcludeFilter,
  sourcesExcludeFilter,
  affiliationCountriesExcludeFilter,
  feedsExcludeFilter,
  geoGroupFilter,
  geoGroupExcludeFilter,
  discreditationsExcludeFilter,
  tagsFilter,
  tagsExcludeFilter,
  affiliationTypesFilter,
  affiliationTypesExcludeFilter,
}) => {
  const { t } = useTranslation();
  const clickRef = useRef();
  const [isActive, setIsActive] = useState(false);
  const [platforms, setPlatforms] = useState(platformsFilter || []);
  const [sourceGroups, setSourceGroups] = useState(sourceGroupsFilter || []);
  const [languages, setLanguages] = useState(languagesFilter || []);
  const [sources, setSources] = useState(sourcesFilter || []);
  const [countries, setCountries] = useState(countriesFilter || []);
  const [sentiment, setSentiment] = useState(sentimentFilter || []);
  const [contentTypes, setContentTypes] = useState(contentTypesFilter || []);
  const [messageStatuses, setMessagesStatuses] = useState(messagesStatusFilter || []);
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(showOnlyFavoritesFilter || false);
  const [showOnlyBots, setShowOnlyBots] = useState(showOnlyBotsFilter || false);
  const [botsExclude, setBotsExclude] = useState(botsExcludeFilter || false);
  const [discreditations, setDiscreditations] = useState(discreditationsFilter || []);
  const [affiliationCountries, setAffiliationCountries] = useState(affiliationCountriesFilter || []);
  const [feeds, setFeeds] = useState(feedsFilter || []);
  const [geoGroup, setGeoGroup] = useState(geoGroupFilter || []);
  const [tags, setTags] = useState(tagsFilter || []);
  const [isPlatformsOpen, setIsPlatformsOpen] = useState(false);
  const [isSourceGroupsOpen, setIsSourceGroupsOpen] = useState(false);
  const [isLanguagesOpen, setIsLanguagesOpen] = useState(false);
  const [isSourcesOpen, setIsSourcesOpen] = useState(false);
  const [isCountriesOpen, setIsCountriesOpen] = useState(false);
  const [isSentimentOpen, setIsSentimentOpen] = useState(false);
  const [isContentTypesOpen, setIsContentTypesOpen] = useState(false);
  const [isMessagesStatusesOpen, setIsMessagesStatusesOpen] = useState(false);
  const [isShowFavoritesOpen, setIsShowFavoritesOpen] = useState(false);
  const [isDiscreditationsOpen, setIsDiscreditationsOpen] = useState(false);
  const [isShowOnlyBotsOpen, setIsShowOnlyBotsOpen] = useState(false);
  const [isAffiliationCountriesOpen, setIsAffiliationCountriesOpen] = useState(false);
  const [isFeedsOpen, setIsFeedsOpen] = useState(false);
  const [isGeoGroupOpen, setIsGeoGroupOpen] = useState(false);
  const [isTagsOpen, setIsTagsOpen] = useState(false);
  const [languagesExclude, setLanguagesExclude] = useState(languagesExcludeFilter || false);
  const [countriesExclude, setCountriesExclude] = useState(countriesExcludeFilter || false);
  const [sourceGroupsExclude, setSourceGroupsExclude] = useState(sourceGroupsExcludeFilter || false);
  const [sourcesExclude, setSourcesExclude] = useState(sourcesExcludeFilter || false);
  const [affiliationCountriesExclude, setAffiliationCountriesExclude] = useState(affiliationCountriesExcludeFilter || false);
  const [feedsExclude, setFeedsExclude] = useState(feedsExcludeFilter || false);
  const [geoGroupExclude, setGeoGroupExclude] = useState(geoGroupExcludeFilter || false);
  const [discreditationsExclude, setDiscreditationsExclude] = useState(discreditationsExcludeFilter || false);
  const [tagsExclude, setTagsExclude] = useState(tagsExcludeFilter || false);
  const [affiliationTypes, setAffiliationTypes] = useState(affiliationTypesFilter || []);
  const [affiliationTypesExclude, setAffiliationTypesExclude] = useState(affiliationTypesExcludeFilter || false);
  const [isAffiliationTypesOpen, setIsAffiliationTypesOpen] = useState(false);

  const [currentUser] = useCurrentUser();
  const workspaceConfig = currentUser?.workspace?.config;
  const featureFlags = workspaceConfig?.featureflags;
  const showFeedsFilter = featureFlags?.includes('show_feeds_filter');
  const hideRiskActors = featureFlags?.includes('hide_risk_actors');
  const showCountryGroupsFilter = featureFlags?.includes('country_groups_filter');
  const showTagsFilter = featureFlags?.includes('show_tags_filter');

  const getActiveExclusiveFilter = () => {
    if (sourceGroups.length > 0) return 'Actor groups';
    if (affiliationCountries.length > 0 || affiliationTypes.length > 0) return 'State-affiliated';
    if (discreditations.length > 0) return 'High risk accounts';
    if (showOnlyBots === true && botsExclude === false) return 'Show only inauthentic actors'; // Only when showing ONLY bots
    return null;
  };

  const activeExclusiveFilter = getActiveExclusiveFilter();

  const isFilterDisabled = (filterName) => {
    return activeExclusiveFilter && activeExclusiveFilter !== filterName;
  };

  const getDisabledTooltip = (filterName) => {
    if (!isFilterDisabled(filterName)) return null;
    return t(`This filter can't be combined with '{{activeFilter}}'`, { activeFilter: t(activeExclusiveFilter) });
  };

  const {
    messages,
    selectedMessages,
    getMessages,
    toggleAll,
    toggleSelected,
    deduplication,
    patchMessages,
    deleteMessages,
    translateMessage,
    toggleTranslate,
    dispatch,
    isLoading,
    setLoading,
  } = useContext(MessagesContext);

  
  let filtersSumm = platforms.length + sourceGroups.length + languages.length + sources.length + countries.length + sentiment.length + contentTypes.length + messageStatuses.length + discreditations.length + (showOnlyBots || botsExclude ? 1 : 0) + affiliationCountries.length + affiliationTypes.length + feeds.length + geoGroup.length + tags.length;

  useClickOutside(clickRef, () => {
    if(isActive) {
      setIsActive(false)
    }
  })

  return (
    <div ref={clickRef} className={styles.root} translate='no'>
      <button 
        className={isActive ? `${styles.button} ${styles.filters} ${styles.active}` : `${styles.button} ${styles.filters}`}
        onClick={() => setIsActive(!isActive)}
      >
        {filtersSumm > 0 ? <span className='summ'>{filtersSumm }</span> : ''}
        {t('Filters')}
        <FilterIcon className={isActive ? 'iconActive' : 'icon'}/>
      </button>

      <div className={isActive ? `${styles.container} ${styles.show}` : `${styles.container}`}>
        <div className={styles.header}>
          <span>{t('Filter by')}</span>

          <span onClick={()=> {
            if (!isPlatformsOpen || !isSourceGroupsOpen || !isLanguagesOpen || !isSourcesOpen || !isCountriesOpen || !isSentimentOpen || !isContentTypesOpen || !isMessagesStatusesOpen || !isShowOnlyBotsOpen || !isDiscreditationsOpen || !isAffiliationCountriesOpen || !isAffiliationTypesOpen || !isFeedsOpen || !isGeoGroupOpen) {
              setIsPlatformsOpen(true);
              setIsSourceGroupsOpen(true);
              setIsLanguagesOpen(true);
              setIsSourcesOpen(true);
              setIsCountriesOpen(true);
              setIsSentimentOpen(true);
              setIsContentTypesOpen(true);
              setIsMessagesStatusesOpen(true);
              setIsFeedsOpen(true);
              setIsShowOnlyBotsOpen(true);
              setIsDiscreditationsOpen(true);
              setIsAffiliationCountriesOpen(true);
              setIsAffiliationTypesOpen(true);
              setIsGeoGroupOpen(true);
            } else {
              setIsPlatformsOpen(false);
              setIsSourceGroupsOpen(false);
              setIsLanguagesOpen(false);
              setIsSourcesOpen(false);
              setIsCountriesOpen(false);
              setIsSentimentOpen(false);
              setIsContentTypesOpen(false);
              setIsMessagesStatusesOpen(false);
              setIsFeedsOpen(false);
              setIsShowOnlyBotsOpen(false);
              setIsDiscreditationsOpen(false);
              setIsAffiliationCountriesOpen(false);
              setIsAffiliationTypesOpen(false);
              setIsGeoGroupOpen(false);
            }
          }}>
            {isPlatformsOpen && isSourceGroupsOpen && isLanguagesOpen && isSourcesOpen && isCountriesOpen && isSentimentOpen && isContentTypesOpen && isMessagesStatusesOpen && isShowOnlyBotsOpen && isDiscreditationsOpen && isAffiliationCountriesOpen && isAffiliationTypesOpen && isFeedsOpen && isGeoGroupOpen ? `${t('Minimize all')}` : `${t('Expand all')}`}
          </span>
        </div>

        <div className={styles.collapsiblesWrapper}>

        { isThreat || deduplication ? '' : <Collapsible
            name='Favorite publications'
            isOpen={isShowFavoritesOpen}
            onChange={(open) => setIsShowFavoritesOpen(open)}
          >
            <FavoriteChooser
              checked={showOnlyFavorites}
              onChange={(value) => setShowOnlyFavorites(value)}
            />
          </Collapsible> }

          { isThreat && !currentUser?.is_super_admin ? '' : <Collapsible
            name='Publications status'
            isOpen={isMessagesStatusesOpen}
            summ={messageStatuses.length}
            onChange={(open) => setIsMessagesStatusesOpen(open)}
          >
            <MessageStatusChooser
              selected={messageStatuses}
              onChange={(checklist) => setMessagesStatuses(checklist)}
            />
          </Collapsible> }

          {deduplication ? '' : <Collapsible 
            name='Platforms'
            summ={platforms.length}
            isOpen={isPlatformsOpen}
            onChange={(open) => setIsPlatformsOpen(open)}
          >
            <PlatformChooser 
              selected={platforms}
              onChange={(checklist) => setPlatforms(checklist)}
            />
          </Collapsible>}
          
          {deduplication ? '' : <Collapsible
            name='Content types'
            summ={contentTypes.length}
            isOpen={isContentTypesOpen}
            onChange={(open) => setIsContentTypesOpen(open)}
          >
            <ContentTypeChooser
              selected={contentTypes}
              onChange={(checklist) => setContentTypes(checklist)}
            />
          </Collapsible>}

          {deduplication ? '' : <Collapsible
            name='Content languages'
            summ={languages.length}
            isOpen={isLanguagesOpen}
            onChange={(open) => setIsLanguagesOpen(open)}
            exclude={languagesExclude}
          >
            <div className={styles.excludeFilterContainer}>
              <ExcludeSwitch
                id='language-switch'
                value={languagesExclude}
                onChange={setLanguagesExclude}
                color='red'
              />
            </div>
            
            <LanguageChooser
              selected={languages}
              onChange={(checklist) => setLanguages(checklist)}
              onClose={() => setIsActive(true)}
              isShare={isShare}
            />
          </Collapsible>}

          {deduplication || !showCountryGroupsFilter ? '' : <Collapsible
            name='Actor origin country groups'
            summ={geoGroup?.length}
            isOpen={isGeoGroupOpen}
            onChange={(open) => setIsGeoGroupOpen(open)}
            exclude={geoGroupExclude}
          >
            <div className={styles.excludeFilterContainer}>
              <ExcludeSwitch
                id='geo-switch'
                value={geoGroupExclude}
                onChange={setGeoGroupExclude}
                color='red'
              />
            </div>
            <GeoGroupFilter
              selected={geoGroup}
              disabled={false}
              onChange={(checklist) => setGeoGroup(checklist)}
              isShare={isShare}
            />
          </Collapsible>}

          {deduplication ? '' : <Collapsible
            name='Actor origin countries'
            summ={countries.length}
            isOpen={isCountriesOpen}
            onChange={(open) => setIsCountriesOpen(open)}
            exclude={countriesExclude}
          >
            <div className={styles.excludeFilterContainer}>
              <ExcludeSwitch
                id='country-switch'
                value={countriesExclude}
                onChange={setCountriesExclude}
                color='red'
              />
            </div>
            
            <CountryChooser
              selected={countries}
              onChange={(checklist) => setCountries(checklist)}
              onClose={() => startTransition(() => setIsActive(true))}
              isShare={isShare}
            />
          </Collapsible>}

          {deduplication || isShare ? '' : <Collapsible 
            name='Actor groups' 
            summ={sourceGroups.length}
            isOpen={isSourceGroupsOpen}
            onChange={(open) => setIsSourceGroupsOpen(open)}
            exclude={sourceGroupsExclude}
            disabled={isFilterDisabled('Actor groups')}
            tooltipText={getDisabledTooltip('Actor groups')}
          >
            <div className={styles.excludeFilterContainer}>
              <ExcludeSwitch
                id='actor-group-switch'
                value={sourceGroupsExclude}
                onChange={setSourceGroupsExclude}
                color='red'
              />
            </div>
            
            <SourceGroupsChooser 
              selected={sourceGroups}
              onChange={(checklist) => setSourceGroups(checklist)}
              onClose={() => setIsActive(true)}
              isShare={isShare}
            />
          </Collapsible>}

          {deduplication ? '' : <Collapsible
            name='Actors'
            summ={sources.length}
            isOpen={isSourcesOpen}
            onChange={(open) => setIsSourcesOpen(open)}
            exclude={sourcesExclude}
          >
            <div className={styles.excludeFilterContainer}>
              <ExcludeSwitch
                id='source-switch'
                value={sourcesExclude}
                onChange={setSourcesExclude}
                color='red'
              />
            </div>
            
            <SourceChooser
              narrativeId={narrativeId}
              selected={sources}
              onChange={(checklist) => setSources(checklist)}
              onClose={() => startTransition(() => setIsActive(true))}
              isShare={isShare}
            />
          </Collapsible>}

          {deduplication ? '' : <Collapsible 
            name='State-affiliated'
            summ={affiliationCountries.length}
            isOpen={isAffiliationCountriesOpen}
            onChange={(open) => setIsAffiliationCountriesOpen(open)}
            exclude={affiliationCountriesExclude}
            disabled={isFilterDisabled('State-affiliated')}
            tooltipText={getDisabledTooltip('State-affiliated')}
          > 
            <AffiliationCountries
              selected={affiliationCountries}
              onChange={(value) => setAffiliationCountries(value)}
              onClose={() => startTransition(() => setIsActive(true))}
              onExcludeChange={(bool) => setAffiliationCountriesExclude(bool)}
              excludeValue={affiliationCountriesExclude}
              isShare={isShare}
            />
          </Collapsible>}

          {deduplication ? '' : <Collapsible
            name='Affiliation Category'
            summ={affiliationTypes.length}
            isOpen={isAffiliationTypesOpen}
            onChange={(open) => setIsAffiliationTypesOpen(open)}
            exclude={affiliationTypesExclude}
            disabled={isFilterDisabled('State-affiliated')}
            tooltipText={getDisabledTooltip('State-affiliated')}
          >
            <div className={styles.excludeFilterContainer}>
              <ExcludeSwitch
                id='affiliation-types-switch'
                value={affiliationTypesExclude}
                onChange={setAffiliationTypesExclude}
                color='red'
                isDisabled={isFilterDisabled('State-affiliated')}
              />
            </div>
            <AffiliationTypesChooser
              selected={affiliationTypes}
              onChange={(checklist) => setAffiliationTypes(checklist)}
            />
          </Collapsible>}

          {deduplication || hideRiskActors ? '' : <Collapsible
            name='High risk accounts'
            summ={discreditations.length}
            isOpen={isDiscreditationsOpen}
            onChange={(open) => setIsDiscreditationsOpen(open)}
            exclude={discreditationsExclude}
            disabled={isFilterDisabled('High risk accounts')}
            tooltipText={getDisabledTooltip('High risk accounts')}
          >
            <div className={styles.excludeFilterContainer}>
              <ExcludeSwitch
                id='discreditations-switch'
                value={discreditationsExclude}
                onChange={setDiscreditationsExclude}
                color='red'
                isDisabled={isFilterDisabled('High risk accounts')}
              />
            </div>
            <IoCChooser
              selected={discreditations}
              onChange={(checklist) => setDiscreditations(checklist)}
            />
          </Collapsible>}

          {deduplication ? '' : <Collapsible 
            name='Inauthentic behavior'
            summ={showOnlyBots === true ? 1 : botsExclude === true ? 1 : 0}
            exclude={botsExclude}
            isOpen={isShowOnlyBotsOpen}
            onChange={(open) => setIsShowOnlyBotsOpen(open)}
          >
            <BotsChooser
              value={showOnlyBots === true  && botsExclude === false ? true : botsExclude === true ? false : null}
              disableShowOnly={isFilterDisabled('Show only inauthentic actors')}
              onChange={(value) => {
                if(value) {
                  setShowOnlyBots(true);
                  setBotsExclude(false);
                }
                if(value === false) {
                  setBotsExclude(true);
                  setShowOnlyBots(true);
                }
                if(value === null) {
                  setShowOnlyBots(false);
                  setBotsExclude(false);
                }
              }}
            />
          </Collapsible>}

          {deduplication ? '' : <Collapsible 
            name='Sentiment'
            isOpen={isSentimentOpen}
            onChange={(open) => setIsSentimentOpen(open)}
          >
            <Sentiment
              selected={sentiment}
              onChange={(checklist) => setSentiment(checklist)}
            />
          </Collapsible>}

          {deduplication || isShare || !showFeedsFilter ? '' : <Collapsible
            name='Feeds'
            summ={feeds.length}
            isOpen={isFeedsOpen}
            onChange={(open) => setIsFeedsOpen(open)}
            exclude={feedsExclude}
          >
            <div className={styles.excludeFilterContainer}>
              <ExcludeSwitch
                id='feeds-switch'
                value={feedsExclude}
                onChange={setFeedsExclude}
                color='red'
              />
            </div>

            <FeedsChooser
              selected={feeds}
              onChange={(checklist) => setFeeds(checklist)}
              onClose={() => setIsActive(true)}
              isShare={isShare}
            />
          </Collapsible>}

          {deduplication || !showTagsFilter ? '' : <Collapsible
            name='Actor tags'
            summ={tags.length}
            isOpen={isTagsOpen}
            onChange={(open) => setIsTagsOpen(open)}
            exclude={tagsExclude}
          >
            <div className={styles.excludeFilterContainer}>
              <ExcludeSwitch
                id='tags-switch'
                value={tagsExclude}
                onChange={setTagsExclude}
                color='red'
              />
            </div>
            <TagsHierarchyChooser
              selected={tags}
              onChange={(checklist) => setTags(checklist)}
              mode="legacy"
            />
          </Collapsible>}
        </div>

        <div className={styles.controls}>
          <button 
            className={`${styles.button} ${styles.apply}`}
            onClick={() => {
              onChange(
                platforms,
                sourceGroups.map(group => group.value),
                languages.map(lang => lang.value),
                sources.map(source => source.value),
                countries.map(country => country.value),
                sentiment,
                contentTypes,
                messageStatuses,
                showOnlyFavorites,
                showOnlyBots,
                discreditations,
                affiliationCountries,
                feeds,
                languagesExclude,
                countriesExclude,
                sourceGroupsExclude,
                sourcesExclude,
                affiliationCountriesExclude,
                feedsExclude,
                geoGroup,
                geoGroupExclude,
                botsExclude,
                discreditationsExclude,
                tags,
                tagsExclude,
                affiliationTypes,
                affiliationTypesExclude,
              )
              setIsActive(false)
            }}
          >
            {t('Apply')}
          </button>

          <button 
            className={`${styles.button} ${styles.reset}`}
            onClick={() => {
              setPlatforms([]);
              setSourceGroups([]);
              setLanguages([]);
              setSources([]);
              setCountries([]);
              setSentiment([]);
              setContentTypes([]);
              setMessagesStatuses([]);
              setShowOnlyFavorites(false);
              setShowOnlyBots(false);
              setDiscreditations([]);
              setAffiliationCountries([]);
              setFeeds([]);
              setLanguagesExclude(false);
              setCountriesExclude(false);
              setSourceGroupsExclude(false);
              setSourcesExclude(false);
              setAffiliationCountriesExclude(false);
              setFeedsExclude(false);
              setGeoGroup([]);
              setGeoGroupExclude(false);
              setBotsExclude(false);
              setDiscreditationsExclude(false);
              setTags([]);
              setTagsExclude(false);
              setAffiliationTypes([]);
              setAffiliationTypesExclude(false);
            }}
          >
            {t('Reset all')}
          </button>
        </div>
      </div>


    </div>
  )
}
